/**
 * Lazy Sentry entry points for modules that live in the entry chunk.
 *
 * main.tsx defers the Sentry SDK to requestIdleCallback so it stays off the
 * first-paint critical path. That deferral was silently defeated: the router,
 * the three error boundaries, two services and the address hook all did
 * `import * as Sentry from "@sentry/react"` at module top level, which made
 * the SDK a static dependency of the first load on every page.
 *
 * Reporting an error is never latency-sensitive, so these wrappers resolve
 * the SDK on demand, and a load only counts as ready once `initSentry()` has
 * succeeded. With the static import, an error reported before the idle-time
 * init was dropped by the SDK ("No client configured on scope"); here the
 * report itself triggers initialisation, so it is delivered.
 *
 * Failure semantics, exactly:
 * - If the SDK chunk fails to load, or `initSentry()` throws, the report that
 *   triggered the load is DROPPED — it is not delivered and not retained. The
 *   failed load is discarded, so the next report (or the next idle preload)
 *   retries loading and initialising from scratch. Breadcrumbs recorded so
 *   far stay queued for that next attempt.
 * - Nothing here throws or rejects to the caller. Monitoring can fail; the
 *   page cannot fail because of it.
 * - A missing DSN is not a failure: the SDK initialises disabled and reports
 *   are no-ops, exactly as with the static import.
 *
 * Use this from modules that are (or may become) part of the entry chunk or
 * of a first-load route. Route-level code that is already lazy can keep
 * importing `@sentry/react` directly.
 */
import type { Breadcrumb, Scope, SeverityLevel } from "@sentry/react";

type SentryClient = typeof import("./sentryClient");

/** Either a `withScope` callback or the object form `Sentry.captureException` accepts. */
export type CaptureContext =
  | ((scope: Scope) => void)
  | {
      level?: SeverityLevel;
      tags?: Record<string, string | number | boolean | null | undefined>;
      extra?: Record<string, unknown>;
    };

const MAX_QUEUED_BREADCRUMBS = 20;

let clientPromise: Promise<SentryClient> | null = null;
/** Set only after `initSentry()` has succeeded; never points at an uninitialised SDK. */
let readyClient: SentryClient | null = null;
const pendingBreadcrumbs: Breadcrumb[] = [];

function loadSentry(): Promise<SentryClient> {
  if (!clientPromise) {
    clientPromise = Promise.all([import("./sentry"), import("./sentryClient")])
      .then(([sentry, loaded]) => {
        // Throws when the SDK cannot be configured. Ordering matters: the
        // client is published only after this returns, so no report is ever
        // handed to an SDK that would silently drop it.
        sentry.initSentry();
        readyClient = loaded;
        for (const breadcrumb of pendingBreadcrumbs.splice(0)) {
          loaded.addBreadcrumb(breadcrumb);
        }
        return loaded;
      })
      .catch((error: unknown) => {
        // Discard the failed load so the next report retries loading AND
        // initialising, instead of reusing a client that never initialised.
        clientPromise = null;
        throw error;
      });
  }
  return clientPromise;
}

function applyContext(scope: Scope, context: CaptureContext): void {
  if (typeof context === "function") {
    context(scope);
    return;
  }
  if (context.level) scope.setLevel(context.level);
  if (context.tags) scope.setTags(context.tags);
  if (context.extra) scope.setExtras(context.extra);
}

/**
 * Load and initialise the SDK ahead of any error. main.tsx calls this from
 * requestIdleCallback; it also drains any breadcrumbs recorded before then.
 */
export function preloadSentry(): void {
  void loadSentry().catch(() => {
    // Monitoring must never take the page down with it.
  });
}

/**
 * Report an exception. `context` is either the callback `Sentry.withScope`
 * would receive or the `{ tags, extra, level }` object `Sentry.captureException`
 * accepts, so call sites migrate without restructuring.
 */
export function captureException(error: unknown, context?: CaptureContext): void {
  void loadSentry()
    .then((Sentry) => {
      if (!context) {
        Sentry.captureException(error);
        return;
      }
      Sentry.withScope((scope) => {
        applyContext(scope, context);
        Sentry.captureException(error);
      });
    })
    .catch(() => {
      // Load or init failed: this report is dropped; the next one retries.
    });
}

export function captureMessage(message: string, level: SeverityLevel = "warning"): void {
  void loadSentry()
    .then((Sentry) => {
      Sentry.captureMessage(message, level);
    })
    .catch(() => {
      // Load or init failed: this report is dropped; the next one retries.
    });
}

/**
 * Record a breadcrumb without forcing the SDK to load. Until the SDK is ready
 * (idle-time preload or the first report) the last few are queued, so they
 * still precede the exception they describe.
 */
export function addBreadcrumb(breadcrumb: Breadcrumb): void {
  if (readyClient) {
    try {
      readyClient.addBreadcrumb(breadcrumb);
    } catch {
      // Monitoring must never take the page down with it.
    }
    return;
  }
  if (pendingBreadcrumbs.length >= MAX_QUEUED_BREADCRUMBS) pendingBreadcrumbs.shift();
  pendingBreadcrumbs.push(breadcrumb);
}
